import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Building2, LayoutGrid, Users, Users2, ChevronRight } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { EmployeeAvatar } from "@/components/EmployeeAvatar";
import { apiGetOrgStats, apiGetDepartments, apiGetEmployees, type EmployeeListItem } from "@/lib/api";
import MobilePeople from "./MobilePeople";

function BarRow({ label, value, max, color }: { label: string; value: number; max: number; color: string }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div>
      <div className="flex justify-between text-xs mb-1">
        <span className="text-foreground/80 truncate">{label}</span>
        <span className="text-muted-foreground shrink-0 ml-2">{value}</span>
      </div>
      <div className="w-full bg-muted rounded-full h-1.5">
        <div className={`${color} rounded-full h-1.5`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function MemberRow({ e }: { e: EmployeeListItem }) {
  return (
    <div className="flex items-center gap-3 px-4 py-2.5 border-b border-border/30 last:border-0">
      <EmployeeAvatar
        avatarUrl={e.avatarUrl}
        avatarColor={e.avatarColor}
        initials={e.initials}
        name={e.name}
        size="sm"
      />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-foreground truncate">{e.name}</p>
        <p className="text-xs text-muted-foreground truncate">{e.jobTitle}</p>
      </div>
    </div>
  );
}

export default function MobileOrganization() {
  const { t } = useTranslation();

  const { data: stats } = useQuery({
    queryKey: ["mobile-org-stats"],
    queryFn: apiGetOrgStats,
    staleTime: 5 * 60_000,
  });

  const { data: departments = [] } = useQuery({
    queryKey: ["mobile-org-departments"],
    queryFn: apiGetDepartments,
    staleTime: 5 * 60_000,
  });

  const { data: employees = [] } = useQuery({
    queryKey: ["mobile-org-employees"],
    queryFn: apiGetEmployees,
    staleTime: 5 * 60_000,
  });

  const [selectedGroup, setSelectedGroup] = useState<{ name: string; members: EmployeeListItem[] } | null>(null);

  const teamGroups = useMemo(() => {
    const map = new Map<string, EmployeeListItem[]>();
    for (const e of employees) {
      const team = e.team?.trim();
      if (!team) continue;
      if (!map.has(team)) map.set(team, []);
      map.get(team)!.push(e);
    }
    return Array.from(map.entries())
      .map(([name, members]) => ({ name, members }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [employees]);

  const maxStatus = Math.max(1, ...(stats?.byStatus.map((s) => s.count) ?? [0]));
  const maxDept = Math.max(1, ...(stats?.byDepartment.map((d) => d.count) ?? [0]));

  return (
    <div className="flex flex-col h-full">
      <Tabs defaultValue="overview" className="flex flex-col h-full overflow-hidden">
        <div className="px-4 pt-4 pb-2 shrink-0">
          <TabsList className="grid grid-cols-4 w-full">
            <TabsTrigger value="overview" className="text-xs px-1">
              <LayoutGrid className="h-3.5 w-3.5 mr-1" />
              {t("mobile.organization.overview")}
            </TabsTrigger>
            <TabsTrigger value="employees" className="text-xs px-1">
              <Users className="h-3.5 w-3.5 mr-1" />
              {t("mobile.organization.employees")}
            </TabsTrigger>
            <TabsTrigger value="departments" className="text-xs px-1">
              <Building2 className="h-3.5 w-3.5 mr-1" />
              {t("mobile.organization.departments")}
            </TabsTrigger>
            <TabsTrigger value="teams" className="text-xs px-1">
              <Users2 className="h-3.5 w-3.5 mr-1" />
              {t("mobile.organization.teams")}
            </TabsTrigger>
          </TabsList>
        </div>

        {/* Overview tab */}
        <TabsContent value="overview" className="flex-1 overflow-y-auto mt-0 px-4 pb-4 space-y-4">
          {stats && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-card border border-border/60 rounded-2xl p-4">
                  <p className="text-2xl font-bold text-foreground leading-none">{stats.totalEmployees}</p>
                  <p className="text-xs text-muted-foreground mt-1">{t("mobile.organization.totalEmployees")}</p>
                </div>
                <div className="bg-card border border-border/60 rounded-2xl p-4">
                  <p className="text-2xl font-bold text-foreground leading-none">{departments.length}</p>
                  <p className="text-xs text-muted-foreground mt-1">{t("mobile.organization.departments")}</p>
                </div>
              </div>

              {stats.byStatus.length > 0 && (
                <div className="bg-card border border-border/60 rounded-2xl p-4 space-y-2.5">
                  <h2 className="text-sm font-semibold text-foreground mb-1">{t("mobile.organization.byStatus")}</h2>
                  {stats.byStatus.map((s) => (
                    <BarRow key={s.status} label={s.status} value={s.count} max={maxStatus} color="bg-primary" />
                  ))}
                </div>
              )}

              {stats.byDepartment.length > 0 && (
                <div className="bg-card border border-border/60 rounded-2xl p-4 space-y-2.5">
                  <h2 className="text-sm font-semibold text-foreground mb-1">{t("mobile.organization.byDepartment")}</h2>
                  {stats.byDepartment.map((d) => (
                    <BarRow key={d.department} label={d.department} value={d.count} max={maxDept} color="bg-violet-500" />
                  ))}
                </div>
              )}

              {stats.recentEmployees.length > 0 && (
                <div className="bg-card border border-border/60 rounded-2xl overflow-hidden">
                  <h2 className="text-sm font-semibold text-foreground px-4 pt-4 pb-2">{t("mobile.organization.recentHires")}</h2>
                  {stats.recentEmployees.map((e) => (
                    <div key={e.id} className="flex items-center gap-3 px-4 py-2.5 border-t border-border/30">
                      <EmployeeAvatar
                        avatarUrl={e.avatarUrl}
                        avatarColor={e.avatarColor}
                        initials={e.initials}
                        name={e.name}
                        size="sm"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">{e.name}</p>
                        <p className="text-xs text-muted-foreground truncate">
                          {e.jobTitle}{e.department ? ` · ${e.department}` : ""}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </TabsContent>

        {/* Employees tab */}
        <TabsContent value="employees" className="flex-1 overflow-hidden mt-0 flex flex-col data-[state=inactive]:hidden">
          <MobilePeople />
        </TabsContent>

        {/* Departments tab */}
        <TabsContent value="departments" className="flex-1 overflow-y-auto mt-0 px-4 pb-4">
          {departments.length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-16 text-muted-foreground">
              <Building2 className="h-10 w-10 opacity-30" />
              <p className="text-sm">{t("mobile.organization.noDepartments")}</p>
            </div>
          ) : (
            <div className="bg-card border border-border/60 rounded-xl overflow-hidden mt-2">
              {departments.map((d) => {
                const members = employees.filter((e) => e.department === d.name);
                return (
                  <button
                    key={d.id}
                    type="button"
                    onClick={() => setSelectedGroup({ name: d.name, members })}
                    className="w-full text-left flex items-center gap-3 px-4 py-3 border-b border-border/30 last:border-0 hover:bg-muted/40 active:bg-muted/60 transition-colors"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground">{d.name}</p>
                      {d.headName && (
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {t("mobile.organization.headOf")}: {d.headName}
                        </p>
                      )}
                      {d.description && (
                        <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{d.description}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5 text-muted-foreground shrink-0">
                      <span className="text-xs">{members.length}</span>
                      <ChevronRight className="h-4 w-4" />
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* Teams tab */}
        <TabsContent value="teams" className="flex-1 overflow-y-auto mt-0 px-4 pb-4">
          {teamGroups.length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-16 text-muted-foreground">
              <Users2 className="h-10 w-10 opacity-30" />
              <p className="text-sm">{t("mobile.organization.noTeams")}</p>
            </div>
          ) : (
            <div className="bg-card border border-border/60 rounded-xl overflow-hidden mt-2">
              {teamGroups.map((g) => (
                <button
                  key={g.name}
                  type="button"
                  onClick={() => setSelectedGroup(g)}
                  className="w-full text-left flex items-center gap-3 px-4 py-3 border-b border-border/30 last:border-0 hover:bg-muted/40 active:bg-muted/60 transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground">{g.name}</p>
                  </div>
                  <div className="flex items-center gap-1.5 text-muted-foreground shrink-0">
                    <span className="text-xs">{g.members.length}</span>
                    <ChevronRight className="h-4 w-4" />
                  </div>
                </button>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Members sheet (shared by Departments & Teams) */}
      <Sheet open={!!selectedGroup} onOpenChange={(open) => { if (!open) setSelectedGroup(null); }}>
        <SheetContent side="bottom" className="rounded-t-2xl max-h-[80vh] flex flex-col p-0 overflow-hidden">
          {selectedGroup && (
            <>
              <SheetHeader className="text-left px-4 pt-5 pb-3 border-b border-border/40 shrink-0">
                <SheetTitle className="text-base">{selectedGroup.name}</SheetTitle>
                <p className="text-sm text-muted-foreground">
                  {t("mobile.organization.memberCount", { count: selectedGroup.members.length })}
                </p>
              </SheetHeader>
              <div className="flex-1 min-h-0 overflow-y-auto">
                {selectedGroup.members.length === 0 ? (
                  <div className="flex flex-col items-center gap-2 py-16 text-muted-foreground">
                    <Users className="h-10 w-10 opacity-30" />
                    <p className="text-sm">{t("mobile.organization.noEmployees")}</p>
                  </div>
                ) : (
                  selectedGroup.members.map((e) => <MemberRow key={e.id} e={e} />)
                )}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
